import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, useAuth, formatIST } from '../lib/store';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { ArrowLeft, Package, Plus, Trash2, Search, RefreshCw, ChevronRight, ChevronDown, History, FileText, AlertCircle, Zap, FileSpreadsheet, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import TallyImportDialog from '../components/admin/TallyImportDialog';
import { logger } from '../lib/logger';

const STOCK_CATEGORIES = ['Plywood', 'Blockboard', 'Doors', 'MDF', 'WPC/PVC'];
const STANDARD_SIZES = ['10x4', '9x4', '8x4', '7x4', '6x4', '8x3', '7x3', '6x3'];

export default function StockManagementPage() {
  const navigate = useNavigate();
  const { user, can } = useAuth();
  const canManageProducts = can('stock.manage');
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterBrand, setFilterBrand] = useState('');
  const [brands, setBrands] = useState([]);
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingStock, setEditingStock] = useState(null);
  const [showTallyImport, setShowTallyImport] = useState(false);

  // Compact-view UX state — persisted so the page comes back the way the
  // user left it on every visit. The default is "compact + hide empty
  // products" so the home view shows only what the warehouse actually has,
  // turning a wall of red zeros into a focused list of real stock.
  const [hideEmpty, setHideEmpty] = useState(() => {
    try { return localStorage.getItem('stockHideEmpty') !== '0'; }
    catch { return true; }
  });
  const [expandedRow, setExpandedRow] = useState(null);
  // Tracks the {stockId} currently being edited inline + its draft value.
  const [inlineEdit, setInlineEdit] = useState({ id: null, value: '' });
  // Low-stock threshold filter — null = no filter, otherwise hide product
  // families whose lowest tracked size is >= threshold. Lets the stock
  // manager instantly answer "what's running low?" without scrolling.
  // Persisted so the daily-check view comes back the way they left it.
  const [lowStockThreshold, setLowStockThreshold] = useState(() => {
    try {
      const v = localStorage.getItem('stockLowThreshold');
      return v ? parseInt(v, 10) : null;
    } catch { return null; }
  });
  useEffect(() => {
    try {
      if (lowStockThreshold == null) localStorage.removeItem('stockLowThreshold');
      else localStorage.setItem('stockLowThreshold', String(lowStockThreshold));
    } catch (e) { /* ignore */ }
  }, [lowStockThreshold]);
  useEffect(() => {
    try { localStorage.setItem('stockHideEmpty', hideEmpty ? '1' : '0'); }
    catch (e) { /* localStorage may be blocked in some browsers — ignore */ }
  }, [hideEmpty]);
  
  // Add Stock Flow States
  const [addStep, setAddStep] = useState(1);
  const [addCategory, setAddCategory] = useState('');
  const [groupedProducts, setGroupedProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selectedProductGroup, setSelectedProductGroup] = useState(null);
  const [expandedThickness, setExpandedThickness] = useState(null);
  const [sizeQuantities, setSizeQuantities] = useState({});

  // Coverage tracking
  const [coverage, setCoverage] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const fetchCoverage = useCallback(async () => {
    try {
      const res = await api.getStockCoverage();
      setCoverage(res.data);
    } catch (err) {
      logger.error('Failed to fetch coverage');
    }
  }, []);

  const handleSyncFromProducts = async () => {
    if (!window.confirm('Auto-create stock entries (physical_stock=0) for every product that doesn\'t have one yet? Your existing stock values will NOT be changed.')) return;
    setSyncing(true);
    try {
      const res = await api.syncStockFromProducts();
      toast.success(res.data.message);
      await fetchCoverage();
      await fetchStocks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleCleanupOrphans = async () => {
    if (!window.confirm('Delete every stock row whose product was already removed from Product Management? Stock for active products will NOT be touched.')) return;
    try {
      const res = await api.cleanupOrphanStock();
      const n = res.data?.deleted_count || 0;
      if (n === 0) toast.success('No orphan stock to clean up — all stock rows are linked to active products');
      else toast.success(`Removed ${n} orphan stock row${n > 1 ? 's' : ''}`);
      await fetchStocks();
      await fetchCoverage();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cleanup failed');
    }
  };

  // Check access
  useEffect(() => {
    if (user && !canManageProducts) {
      toast.error('Access denied. Admin or Manager only.');
      navigate('/');
    }
  }, [user, canManageProducts, navigate]);

  const fetchStocks = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterCategory) params.category = filterCategory;
      if (filterBrand) params.brand_name = filterBrand;
      const res = await api.getStockList(params);
      setStocks(res.data);
    } catch (err) {
      toast.error('Failed to load stock data');
    } finally {
      setLoading(false);
    }
  }, [filterCategory, filterBrand]);

  const fetchBrands = useCallback(async () => {
    if (!filterCategory) {
      setBrands([]);
      return;
    }
    try {
      const res = await api.getStockBrands(filterCategory);
      setBrands(res.data.brands || []);
    } catch (err) {
      logger.error('Failed to load brands');
    }
  }, [filterCategory]);

  useEffect(() => {
    fetchStocks();
    fetchCoverage();
  }, [fetchStocks, fetchCoverage]);

  useEffect(() => {
    fetchBrands();
  }, [fetchBrands]);

  // Fetch and group products - matching the Products page structure
  const fetchCategoryProducts = async (category) => {
    setLoadingProducts(true);
    try {
      const res = await api.getProducts({ category, limit: 500 });
      const products = res.data.products || [];
      
      // Group products by brand_name + subcategory + grade (matching Products page)
      // This creates cards like "ARCHIDPLY - Classic" with "MR" grade
      const grouped = {};
      
      products.forEach(product => {
        // Build display name like "BRAND - Subcategory" (same as Products page)
        const displayName = product.subcategory 
          ? `${product.brand_name} - ${product.subcategory}`
          : product.brand_name;
        const grade = product.grade || '';
        const key = `${displayName}|${grade}`;
        
        if (!grouped[key]) {
          grouped[key] = {
            brand_name: product.brand_name,  // This is what links to quotations!
            subcategory: product.subcategory,
            display_name: displayName,
            grade: grade,
            category: category,
            thicknesses: {} // { '18MM': { available_sizes: [...], product_id: '...' } }
          };
        }
        
        // Add this thickness with its available sizes
        const thickness = product.thickness;
        if (thickness && !grouped[key].thicknesses[thickness]) {
          grouped[key].thicknesses[thickness] = {
            available_sizes: product.available_sizes || STANDARD_SIZES,
            product_id: product.id,
            base_rate: product.base_rate
          };
        }
      });
      
      // Convert to array and sort
      const groupedArray = Object.values(grouped).sort((a, b) => 
        a.display_name.localeCompare(b.display_name)
      );
      
      setGroupedProducts(groupedArray);
    } catch (err) {
      toast.error('Failed to load products');
      setGroupedProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  // Handle category selection
  const handleCategorySelect = (category) => {
    setAddCategory(category);
    fetchCategoryProducts(category);
    setAddStep(2);
  };

  // Handle product selection
  const handleProductSelect = (productGroup) => {
    setSelectedProductGroup(productGroup);
    setSizeQuantities({});
    setExpandedThickness(null);
    setAddStep(3);
  };

  // Toggle thickness expansion
  const toggleThickness = (thickness) => {
    setExpandedThickness(expandedThickness === thickness ? null : thickness);
  };

  // Update quantity for a specific thickness + size
  const updateQuantity = (thickness, size, value) => {
    const key = `${thickness}|${size}`;
    setSizeQuantities(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Save stock entries - includes subcategory and grade for full product name
  const handleSaveStock = async () => {
    if (!selectedProductGroup) return;
    
    const entriesToSave = Object.entries(sizeQuantities)
      .filter(([key, qty]) => qty !== '' && parseInt(qty) >= 0)
      .map(([key, qty]) => {
        const [thickness, size] = key.split('|');
        return {
          brand_name: selectedProductGroup.brand_name,
          subcategory: selectedProductGroup.subcategory || '',
          grade: selectedProductGroup.grade || '',
          category: selectedProductGroup.category,
          thickness: thickness,
          size: size,
          physical_stock: parseInt(qty) || 0
        };
      });
    
    if (entriesToSave.length === 0) {
      toast.error('Please enter at least one quantity');
      return;
    }
    
    try {
      let successCount = 0;
      for (const entry of entriesToSave) {
        await api.quickUpdateStock({
          brand_name: entry.brand_name,
          subcategory: entry.subcategory,
          grade: entry.grade,
          category: entry.category,
          thickness: entry.thickness,
          size: entry.size,
          physical_stock: entry.physical_stock
        });
        successCount++;
      }
      toast.success(`Stock updated for ${successCount} entries`);
      // Go back to product selection (step 2) instead of closing completely
      setSelectedProductGroup(null);
      setSizeQuantities({});
      setExpandedThickness(null);
      setAddStep(2);
      fetchStocks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save stock');
    }
  };

  // Close and reset add dialog
  const closeAddDialog = () => {
    setShowAddDialog(false);
    setAddStep(1);
    setAddCategory('');
    setGroupedProducts([]);
    setSelectedProductGroup(null);
    setSizeQuantities({});
    setExpandedThickness(null);
  };

  const handleDeleteStock = async (stockId) => {
    if (!window.confirm('Delete this stock entry?')) return;
    try {
      await api.deleteStock(stockId);
      toast.success('Stock entry deleted');
      fetchStocks();
    } catch (err) {
      toast.error('Failed to delete stock');
    }
  };

  // Inline edit handler: save a single stock cell's new physical_stock without
  // opening the full edit dialog. Triggered by Enter / blur from the cell
  // input. Failures roll the input back to the prior value via re-fetch.
  const handleInlineSave = async (stock, draftValue) => {
    const newVal = parseInt(draftValue, 10);
    if (isNaN(newVal) || newVal < 0) {
      setInlineEdit({ id: null, value: '' });
      return;
    }
    if (newVal === stock.physical_stock) {
      setInlineEdit({ id: null, value: '' });
      return;
    }
    try {
      await api.updateStock(stock.id, { physical_stock: newVal });
      toast.success(`${stock.brand_name} ${stock.thickness} ${stock.size} → ${newVal}`);
      setInlineEdit({ id: null, value: '' });
      fetchStocks();
    } catch (err) {
      toast.error('Failed to update stock');
      setInlineEdit({ id: null, value: '' });
    }
  };

  const handleUpdateStock = async () => {
    if (!editingStock) return;
    try {
      await api.updateStock(editingStock.id, { physical_stock: editingStock.physical_stock });
      toast.success('Stock updated');
      setShowEditDialog(false);
      setEditingStock(null);
      fetchStocks();
    } catch (err) {
      toast.error('Failed to update stock');
    }
  };

  // Filter stocks by search
  const filteredStocks = stocks.filter(s => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return s.brand_name.toLowerCase().includes(q) || 
           (s.subcategory || '').toLowerCase().includes(q) ||
           (s.grade || '').toLowerCase().includes(q) ||
           s.thickness.toLowerCase().includes(q) ||
           s.size.toLowerCase().includes(q);
  });

  // Build full display name for stock entry
  const getStockDisplayName = (stock) => {
    let name = stock.brand_name;
    if (stock.subcategory) name += ` - ${stock.subcategory}`;
    if (stock.grade) name += ` ${stock.grade}`;
    return name;
  };

  // Group stocks by full product name (brand + subcategory + grade) and thickness
  const groupedStocks = {};
  filteredStocks.forEach(stock => {
    const displayName = getStockDisplayName(stock);
    const key = `${stock.category}|${displayName}|${stock.thickness}`;
    if (!groupedStocks[key]) {
      groupedStocks[key] = {
        category: stock.category,
        brand_name: stock.brand_name,
        subcategory: stock.subcategory || '',
        grade: stock.grade || '',
        display_name: displayName,
        thickness: stock.thickness,
        sizes: {},
        total_physical: 0,
        total_free: 0,
      };
    }
    groupedStocks[key].sizes[stock.size] = stock;
    groupedStocks[key].total_physical += (stock.physical_stock || 0);
    groupedStocks[key].total_free += (stock.free_stock || 0);
  });

  // ─── Top-of-page summary stats ─────────────────────────────────────────
  // Cheap counters over the (already-filtered) stock rows. Updated whenever
  // filters change so the chip values always match what's visible below.
  const summary = filteredStocks.reduce((acc, s) => {
    const free = s.free_stock || 0;
    if (free > 20) acc.healthy += 1;
    else if (free > 0) acc.low += 1;
    else if (free === 0) acc.empty += 1;
    else acc.negative += 1;
    return acc;
  }, { healthy: 0, low: 0, empty: 0, negative: 0 });

  // Build the list to render — sorted, optionally hiding all-zero groups so
  // the daily-update view focuses on entries the stock manager actually
  // needs to look at. When a low-stock threshold is set, also restrict to
  // groups containing at least one tracked size whose free_stock is below
  // the threshold — i.e. things the manager needs to act on now.
  const visibleGroups = Object.values(groupedStocks)
    .filter((g) => {
      // When a low-stock threshold is set, the user explicitly wants to
      // surface items below it — out-of-stock products MUST appear too,
      // so the threshold filter overrides the hide-empty preference.
      if (lowStockThreshold != null) {
        return Object.values(g.sizes).some((s) => (s.free_stock || 0) < lowStockThreshold);
      }
      if (hideEmpty && g.total_physical === 0 && g.total_free === 0) return false;
      return true;
    })
    .sort((a, b) => {
      // Category → brand → subcategory → thickness (numeric) so two entries
      // for the same product family always sit together.
      const cat = a.category.localeCompare(b.category);
      if (cat !== 0) return cat;
      const dn = a.display_name.localeCompare(b.display_name);
      if (dn !== 0) return dn;
      const ta = parseFloat(a.thickness) || 0;
      const tb = parseFloat(b.thickness) || 0;
      return ta - tb;
    });

  const getStockColor = (free) => {
    if (free < 0) return 'bg-red-200 text-red-700 border-red-700 font-bold';
    if (free === 0) return 'bg-red-50 text-red-700 border-red-200';
    if (free <= 20) return 'bg-amber-50 text-amber-700 border-amber-200';
    return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  };

  // Sort thicknesses numerically
  const sortThicknesses = (thicknesses) => {
    return Object.keys(thicknesses).sort((a, b) => {
      const numA = parseInt(a) || 0;
      const numB = parseInt(b) || 0;
      return numA - numB;
    });
  };

  return (
    <div data-testid="stock-management-page">
      {/* Header */}
      <div className="flex flex-col gap-3 mb-6 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-3">
          <Button className="rounded-xl" variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="font-display text-[20px] font-bold text-heda-ink flex items-center gap-2">
              <Package className="w-5 h-5 text-heda-gold" /> Stock Management
            </h1>
            <p className="text-[13px] text-heda-mute">
              Track physical stock for Plywood, Blockboard, Doors, MDF, WPC/PVC
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button className="rounded-xl bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold" variant="outline" onClick={() => setShowTallyImport(true)} data-testid="tally-import-btn">
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Import from Tally
          </Button>
          <Button className="rounded-xl bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold" variant="outline" onClick={() => navigate('/stock/history')} data-testid="stock-history-btn">
            <History className="w-4 h-4 mr-2" /> History
          </Button>
          <Button className="rounded-xl bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold" variant="outline" onClick={() => navigate('/purchase-orders')} data-testid="purchase-orders-btn">
            <FileText className="w-4 h-4 mr-2" /> Purchase Orders
          </Button>
          <Button onClick={() => setShowAddDialog(true)} className="rounded-xl bg-heda-ink hover:bg-black" data-testid="add-stock-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Stock
          </Button>
        </div>
      </div>

      {/* Product Linking Coverage Banner */}
      {coverage && coverage.missing_count > 0 && (
        <Card className="rounded-2xl shadow-none mb-4 border-amber-200 bg-amber-50" data-testid="coverage-banner">
          <CardContent className="py-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-700 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-amber-700">
                    {coverage.missing_count} products not linked to stock yet
                  </p>
                  <p className="text-xs text-amber-700">
                    Coverage: {coverage.coverage_pct}% ({coverage.existing_count}/{coverage.total_expected}).
                    One-click sync will create 0-stock entries for all missing products so every Plywood/Door/MDF/Blockboard/WPC-PVC product is tracked.
                  </p>
                </div>
              </div>
              <Button
                onClick={handleSyncFromProducts}
                disabled={syncing}
                className="rounded-xl bg-amber-700 hover:bg-amber-800 text-white"
                data-testid="sync-stock-btn"
              >
                <Zap className="w-4 h-4 mr-2" />
                {syncing ? 'Syncing...' : `Sync ${coverage.missing_count} Products`}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Orphan stock cleanup — always visible to admin/manager. Removes
          stock rows whose underlying product was deleted from Product
          Management (e.g. "ABC Doors" / "ABC MDF" rows that lingered after
          the product was removed). */}
      <div className="mb-6 flex justify-end">
        <Button
          variant="outline"
          onClick={handleCleanupOrphans}
          className="rounded-xl text-red-700 border-red-200 hover:bg-red-50"
          data-testid="cleanup-orphan-stock-btn"
        >
          Cleanup orphan stock rows
        </Button>
      </div>

      {/* Filters */}
      <Card className="rounded-2xl bg-white shadow-none mb-4 border-heda-line">
        <CardContent className="py-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-heda-mute" />
                <Input 
                  placeholder="Search brand, thickness..." 
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="rounded-xl bg-heda-cream/50 pl-9"
                  data-testid="stock-search-input"
                />
              </div>
            </div>
            <div className="w-[160px]">
              <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Category</Label>
              <Select value={filterCategory || 'all'} onValueChange={v => { setFilterCategory(v === 'all' ? '' : v); setFilterBrand(''); }}>
                <SelectTrigger className="rounded-xl bg-heda-cream/50" data-testid="category-filter">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {STOCK_CATEGORIES.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {filterCategory && brands.length > 0 && (
              <div className="w-[180px]">
                <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Brand</Label>
                <Select value={filterBrand || 'all'} onValueChange={v => setFilterBrand(v === 'all' ? '' : v)}>
                  <SelectTrigger className="rounded-xl bg-heda-cream/50" data-testid="brand-filter">
                    <SelectValue placeholder="All Brands" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Brands</SelectItem>
                    {brands.map(b => (
                      <SelectItem key={b} value={b}>{b}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <label className="flex items-center gap-2 text-sm text-heda-ink cursor-pointer select-none h-9" data-testid="hide-empty-toggle-label">
              <input
                type="checkbox"
                checked={hideEmpty}
                onChange={(e) => setHideEmpty(e.target.checked)}
                className="rounded-lg accent-heda-olive"
                data-testid="hide-empty-toggle"
              />
              Hide empty products
            </label>
            <Button className="rounded-xl bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold" variant="outline" onClick={fetchStocks} size="sm" data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Quick-stats banner — at-a-glance counts of healthy / low / out so the
          stock manager can immediately see what needs attention without
          scrolling through hundreds of products. Counts respect filters.
          The chips are CLICKABLE filters: click "low" to instantly hide
          everything except low-stock items, click "out of stock" for only
          empty rows, click the same chip again to clear. */}
      {!loading && filteredStocks.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-2 text-xs items-center" data-testid="stock-stats-bar">
          <span
            className="px-3 py-1.5 rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-200"
            data-testid="stat-chip-healthy"
          >
            <strong>{summary.healthy}</strong> healthy (&gt;20)
          </span>
          <button
            onClick={() => setLowStockThreshold(lowStockThreshold === 21 ? null : 21)}
            className={`px-3 py-1.5 rounded-xl border transition-colors cursor-pointer ${
              lowStockThreshold === 21
                ? 'bg-amber-200 text-amber-700 border-amber-200 ring-2 ring-amber-200'
                : 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-50'
            }`}
            data-testid="stat-chip-low"
            title="Click to show only items with stock below 21"
          >
            <strong>{summary.low + summary.empty + summary.negative}</strong> needs attention (&lt;21)
          </button>
          <button
            onClick={() => setLowStockThreshold(lowStockThreshold === 1 ? null : 1)}
            className={`px-3 py-1.5 rounded-xl border transition-colors cursor-pointer ${
              lowStockThreshold === 1
                ? 'bg-red-200 text-red-700 border-red-200 ring-2 ring-red-200'
                : 'bg-red-50 text-red-700 border-red-200 hover:bg-red-50'
            }`}
            data-testid="stat-chip-out"
            title="Click to show only items that are out of stock or owed"
          >
            <strong>{summary.empty + summary.negative}</strong> out of stock
          </button>
          {summary.negative > 0 && (
            <span className="px-3 py-1.5 rounded-xl bg-red-50 text-red-700 border border-red-200">
              <strong>{summary.negative}</strong> owed (negative)
            </span>
          )}
          {/* Custom threshold dropdown — finer control than the chips above.
              Setting it overrides the chips' "healthy/low/out" presets. */}
          <div className="flex items-center gap-1 ml-1" data-testid="threshold-picker">
            <span className="text-heda-sub">Show below:</span>
            {[null, 10, 20, 50, 100].map((t) => (
              <button
                key={t === null ? 'all' : t}
                onClick={() => setLowStockThreshold(t)}
                className={`px-2 py-1 rounded text-xs border transition-colors ${
                  lowStockThreshold === t
                    ? 'bg-heda-ink text-white border-heda-ink'
                    : 'bg-white text-heda-sub border-heda-line hover:border-heda-gold'
                }`}
                data-testid={`threshold-${t === null ? 'all' : t}`}
              >
                {t === null ? 'All' : `< ${t}`}
              </button>
            ))}
          </div>
          <span className="px-3 py-1.5 rounded-xl bg-heda-cream/60 text-heda-sub border border-heda-line ml-auto">
            Showing <strong>{visibleGroups.length}</strong> of <strong>{Object.keys(groupedStocks).length}</strong> products
          </span>
        </div>
      )}

      {/* Active-filter indicator — when a threshold is set, show a clear
          banner so the stock manager always knows why the list is short. */}
      {lowStockThreshold != null && (
        <div className="mb-3 flex items-center justify-between px-3 py-2 rounded-xl bg-amber-50 border border-amber-200 text-xs" data-testid="threshold-active-banner">
          <span className="text-amber-700">
            <strong>Filter active:</strong> showing only products with at least one size below{' '}
            {lowStockThreshold === 1 ? '1 (out of stock / owed)' : `${lowStockThreshold} sheets`}
          </span>
          <button
            onClick={() => setLowStockThreshold(null)}
            className="text-amber-700 hover:text-amber-700 underline font-medium"
            data-testid="clear-threshold-btn"
          >
            Clear filter
          </button>
        </div>
      )}

      {/* Stock list — compact pill rows. Empty/zero size cells are hidden
          by default to cut visual noise; click a row to expand into the
          full grid so the stock manager can edit every size including
          ones currently at 0. */}
      {loading ? (
        <div className="flex items-center justify-center gap-2 py-10 text-sm text-heda-mute"><Loader2 className="h-5 w-5 animate-spin text-heda-gold" /> Loading...</div>
      ) : visibleGroups.length === 0 ? (
        <Card className="rounded-2xl bg-white shadow-none border-heda-line">
          <CardContent className="py-10 text-center">
            <Package className="w-12 h-12 mx-auto text-heda-mute mb-3" />
            <p className="text-heda-mute">
              {Object.keys(groupedStocks).length > 0
                ? 'No products to show with current filters — uncheck \u201CHide empty products\u201D to see all entries.'
                : 'No stock entries found'}
            </p>
            <p className="text-sm text-heda-mute mt-1">Click &quot;Add Stock&quot; to add stock for your products</p>
          </CardContent>
        </Card>
      ) : (
        <div className="border border-heda-line rounded-xl bg-white divide-y divide-heda-line" data-testid="stock-compact-list">
          {visibleGroups.map((group, idx) => {
            const groupKey = `${group.category}|${group.display_name}|${group.thickness}`;
            const isExpanded = expandedRow === groupKey;
            const sizesWithStock = Object.values(group.sizes).filter((s) => (s.physical_stock || 0) > 0 || (s.free_stock || 0) !== 0);
            return (
              <div key={groupKey} className="hover:bg-heda-cream/60 transition-colors" data-testid={`stock-row-${idx}`}>
                <div
                  className="flex items-center gap-3 px-3 py-2 cursor-pointer"
                  onClick={() => setExpandedRow(isExpanded ? null : groupKey)}
                >
                  <Badge variant="outline" className="rounded-full text-[10px] flex-shrink-0 uppercase tracking-wide">
                    {group.category}
                  </Badge>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-heda-ink truncate">
                      {group.display_name} <span className="text-heda-olive-dark">· {group.thickness}</span>
                    </div>
                  </div>
                  {/* Inline pills — one per size that actually has stock.
                      All-zero sizes are hidden here; expand the row to edit them. */}
                  <div className="flex items-center gap-1.5 flex-wrap justify-end max-w-[55%]">
                    {sizesWithStock.length === 0 ? (
                      <span className="text-xs text-heda-mute italic">no stock</span>
                    ) : sizesWithStock.map((stock) => {
                      const free = stock.free_stock || 0;
                      const pillStyle = free < 0
                        ? 'bg-red-50 text-red-700 border-red-200'
                        : free === 0
                          ? 'bg-red-50 text-red-700 border-red-200'
                          : free <= 20
                            ? 'bg-amber-50 text-amber-700 border-amber-200'
                            : 'bg-emerald-50 text-emerald-700 border-emerald-200';
                      return (
                        <span
                          key={stock.id}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg border text-xs ${pillStyle}`}
                          title={`Quoted: ${stock.quoted_stock || 0} | Free: ${free}`}
                          data-testid={`stock-pill-${stock.id}`}
                        >
                          <span className="font-mono text-[10px] text-heda-sub">{stock.size}</span>
                          <span className="font-bold">{stock.physical_stock}</span>
                        </span>
                      );
                    })}
                    <span className="px-2 py-0.5 rounded-lg bg-heda-cream/60 text-heda-sub border border-heda-line text-xs">
                      total <strong>{group.total_physical}</strong>
                    </span>
                  </div>
                  {isExpanded ? <ChevronDown className="w-4 h-4 text-heda-mute" /> : <ChevronRight className="w-4 h-4 text-heda-mute" />}
                </div>
                {/* Expanded body — full editable grid. Click a cell qty to
                    flip it into an inline number input; Enter / blur saves. */}
                {isExpanded && (
                  <div className="px-3 pb-3 pt-1 bg-heda-cream/60" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[11px] text-heda-mute">Click any size to edit · Q = quoted (reserved) · F = free to quote</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="rounded-xl h-7 px-2 text-heda-olive-dark hover:bg-heda-olive-tint"
                        onClick={() => navigate(`/stock/history?category=${encodeURIComponent(group.category)}&brand=${encodeURIComponent(group.brand_name)}&thickness=${encodeURIComponent(group.thickness)}`)}
                        data-testid={`group-history-btn-${idx}`}
                      >
                        <History className="w-3.5 h-3.5 mr-1" />
                        <span className="text-xs">History</span>
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                      {STANDARD_SIZES.map((size) => {
                        const stock = group.sizes[size];
                        if (!stock) {
                          return (
                            <div key={size} className="p-2 border border-dashed border-heda-line rounded-lg bg-white text-center opacity-60">
                              <div className="text-xs font-medium text-heda-mute">{size}</div>
                              <div className="text-xs text-heda-mute">—</div>
                            </div>
                          );
                        }
                        const free = stock.free_stock || 0;
                        const cellStyle = free < 0
                          ? 'bg-red-50 border-red-200 text-red-700'
                          : free === 0
                            ? 'bg-red-50 border-red-200 text-red-700'
                            : free <= 20
                              ? 'bg-amber-50 border-amber-200 text-amber-700'
                              : 'bg-emerald-50 border-emerald-200 text-emerald-700';
                        const isEditing = inlineEdit.id === stock.id;
                        return (
                          <div
                            key={size}
                            className={`p-2 border rounded-lg text-center cursor-pointer hover:shadow transition-shadow ${cellStyle}`}
                            onClick={() => {
                              if (isEditing) return;
                              setInlineEdit({ id: stock.id, value: String(stock.physical_stock) });
                            }}
                            data-testid={`stock-cell-${stock.id}`}
                          >
                            <div className="text-xs font-bold">{size}</div>
                            {isEditing ? (
                              <input
                                autoFocus
                                type="number"
                                value={inlineEdit.value}
                                onChange={(e) => setInlineEdit({ id: stock.id, value: e.target.value })}
                                onBlur={() => handleInlineSave(stock, inlineEdit.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleInlineSave(stock, inlineEdit.value);
                                  if (e.key === 'Escape') setInlineEdit({ id: null, value: '' });
                                }}
                                className="w-full text-center text-base font-bold bg-white border border-heda-gold rounded-lg mt-0.5 outline-none"
                                data-testid={`stock-cell-input-${stock.id}`}
                              />
                            ) : (
                              <div className="text-lg font-bold">{stock.physical_stock}</div>
                            )}
                            <div className="text-[10px] leading-tight">
                              Q: {stock.quoted_stock || 0} | F: {free}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Legend */}
      <div className="mt-4 flex items-center gap-4 text-xs text-heda-mute flex-wrap">
        <span className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-lg bg-emerald-50 border border-emerald-200"></div> Good (&gt;20)
        </span>
        <span className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-lg bg-amber-50 border border-amber-200"></div> Low (1–20)
        </span>
        <span className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-lg bg-red-50 border border-red-200"></div> Out of stock
        </span>
        <span className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-lg bg-red-50 border-2 border-red-200"></div> Owed (negative)
        </span>
        <span className="ml-4">Tip: click any product row to expand and edit every size inline</span>
      </div>

      {/* Add Stock Dialog */}
      <Dialog open={showAddDialog} onOpenChange={(open) => { if (!open) closeAddDialog(); else setShowAddDialog(true); }}>
        <DialogContent className="border-heda-line bg-white sm:rounded-3xl sm:max-w-lg max-h-[85vh] overflow-hidden flex flex-col" data-testid="add-stock-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {addStep > 1 && (
                <Button variant="ghost" size="sm" className="rounded-xl h-6 w-6 p-0" onClick={() => setAddStep(addStep - 1)}>
                  <ArrowLeft className="w-4 h-4" />
                </Button>
              )}
              {addStep === 1 && 'Select Category'}
              {addStep === 2 && `Select Product - ${addCategory}`}
              {addStep === 3 && 'Add Stock Quantities'}
            </DialogTitle>
            {addStep === 3 && selectedProductGroup && (
              <DialogDescription className="text-left">
                <span className="font-semibold text-heda-ink">{selectedProductGroup.display_name}</span>
                {selectedProductGroup.grade && (
                  <span className="ml-2 text-heda-sub">{selectedProductGroup.grade}</span>
                )}
              </DialogDescription>
            )}
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-2">
            {/* Step 1: Select Category */}
            {addStep === 1 && (
              <div className="grid grid-cols-2 gap-3">
                {STOCK_CATEGORIES.map(cat => (
                  <button
                    key={cat}
                    onClick={() => handleCategorySelect(cat)}
                    className="p-4 border-2 border-heda-line rounded-xl hover:border-heda-gold hover:bg-heda-cream transition-all text-left group"
                    data-testid={`category-${cat}`}
                  >
                    <div className="font-semibold text-heda-ink group-hover:text-heda-olive-dark">{cat}</div>
                    <div className="text-xs text-heda-mute mt-1">Click to view products</div>
                    <ChevronRight className="w-4 h-4 text-heda-mute group-hover:text-heda-olive-dark mt-2" />
                  </button>
                ))}
              </div>
            )}

            {/* Step 2: Select Product - Matching Products page layout */}
            {addStep === 2 && (
              <div className="space-y-3">
                {loadingProducts ? (
                  <div className="text-center py-8 text-heda-mute">Loading products...</div>
                ) : groupedProducts.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-heda-mute">No products found in {addCategory}</p>
                    <p className="text-sm text-heda-mute mt-1">Add products first in the Product Management section</p>
                  </div>
                ) : (
                  groupedProducts.map((productGroup, idx) => (
                    <button
                      key={productGroup.id || productGroup.display_name || `pg-${idx}`}
                      onClick={() => handleProductSelect(productGroup)}
                      className="w-full p-4 border-2 border-heda-line rounded-xl hover:border-heda-gold hover:bg-heda-cream transition-all text-left"
                      data-testid={`product-${idx}`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          {/* Product Name - matching Products page style */}
                          <div className="font-bold text-heda-ink">{productGroup.display_name}</div>
                          {/* Grade below */}
                          {productGroup.grade && (
                            <div className="text-sm text-heda-sub mt-0.5">{productGroup.grade}</div>
                          )}
                          {/* Available Thicknesses */}
                          <div className="text-xs text-heda-mute mt-2 uppercase tracking-wide">Available Thicknesses</div>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {sortThicknesses(productGroup.thicknesses).map(t => (
                              <Badge key={t} className="rounded-full bg-heda-ink text-white text-xs">{t}</Badge>
                            ))}
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-heda-mute mt-1" />
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}

            {/* Step 3: Enter Quantities - Only show available sizes */}
            {addStep === 3 && selectedProductGroup && (
              <div className="space-y-3">
                <div className="text-xs text-heda-mute">
                  Click on a thickness to expand and enter quantities for available sizes
                </div>
                
                <div className="space-y-2">
                  {sortThicknesses(selectedProductGroup.thicknesses).map(thickness => {
                    const thicknessData = selectedProductGroup.thicknesses[thickness];
                    const isExpanded = expandedThickness === thickness;
                    // Use ONLY the available sizes from the product, not standard sizes
                    const availableSizes = thicknessData.available_sizes || [];
                    
                    return (
                      <div key={thickness} className="border rounded-xl overflow-hidden">
                        {/* Thickness Header */}
                        <button
                          onClick={() => toggleThickness(thickness)}
                          className={`w-full p-3 flex items-center justify-between transition-all ${
                            isExpanded ? 'bg-heda-ink text-white' : 'bg-heda-cream/60 hover:bg-heda-line'
                          }`}
                          data-testid={`thickness-${thickness}`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="font-bold">{thickness}</span>
                            <span className={`text-xs ${isExpanded ? 'text-white/70' : 'text-heda-mute'}`}>
                              ({availableSizes.length} sizes)
                            </span>
                          </div>
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                        </button>
                        
                        {/* Available Sizes Grid - ONLY product's available sizes */}
                        {isExpanded && (
                          <div className="p-3 bg-white border-t">
                            {availableSizes.length === 0 ? (
                              <p className="text-sm text-heda-mute">No sizes configured for this thickness</p>
                            ) : (
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                {availableSizes.map(size => {
                                  const key = `${thickness}|${size}`;
                                  return (
                                    <div key={size} className="flex items-center gap-2">
                                      <Label className="w-10 text-xs font-bold text-heda-sub">{size}</Label>
                                      <Input
                                        type="number"
                                        min="0"
                                        placeholder="0"
                                        value={sizeQuantities[key] || ''}
                                        onChange={e => updateQuantity(thickness, size, e.target.value)}
                                        className="rounded-xl bg-heda-cream/50 h-8 text-center text-sm font-bold"
                                        data-testid={`qty-${thickness}-${size}`}
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Footer buttons for step 3 */}
          {addStep === 3 && (
            <div className="flex gap-2 pt-3 border-t">
              <Button onClick={handleSaveStock} className="rounded-xl flex-1 bg-heda-ink hover:bg-black" data-testid="save-stock-btn">
                Save Stock
              </Button>
              <Button className="rounded-xl bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold" variant="outline" onClick={closeAddDialog}>Cancel</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Stock Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="border-heda-line bg-white sm:rounded-3xl sm:max-w-sm" data-testid="edit-stock-dialog">
          <DialogHeader>
            <DialogTitle>Update Stock</DialogTitle>
            <DialogDescription>
              {editingStock && `${editingStock.brand_name} ${editingStock.thickness} ${editingStock.size}`}
            </DialogDescription>
          </DialogHeader>
          {editingStock && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-3 gap-2 text-center text-sm">
                <div className="p-2 bg-sky-50 rounded-lg">
                  <div className="text-xs text-heda-mute">Physical</div>
                  <div className="font-bold text-sky-700">{editingStock.physical_stock}</div>
                </div>
                <div className="p-2 bg-amber-50 rounded-lg">
                  <div className="text-xs text-heda-mute">Quoted</div>
                  <div className="font-bold text-amber-700">{editingStock.quoted_stock}</div>
                </div>
                <div className={`p-2 rounded-lg ${editingStock.free_stock > 0 ? 'bg-emerald-50' : 'bg-red-50'}`}>
                  <div className="text-xs text-heda-mute">Free</div>
                  <div className={`font-bold ${editingStock.free_stock > 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                    {editingStock.free_stock}
                  </div>
                </div>
              </div>
              <div>
                <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">New Physical Stock</Label>
                <Input 
                  type="number" 
                  min="0"
                  value={editingStock.physical_stock}
                  onChange={e => setEditingStock({...editingStock, physical_stock: parseInt(e.target.value) || 0})}
                  className="rounded-xl bg-heda-cream/50 text-center text-lg font-bold"
                  data-testid="edit-stock-input"
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleUpdateStock} className="rounded-xl flex-1 bg-heda-ink hover:bg-black" data-testid="update-stock-btn">
                  Update
                </Button>
                <Button 
                  variant="outline" 
                  className="rounded-xl text-red-700 border-red-200 hover:bg-red-50"
                  onClick={() => { handleDeleteStock(editingStock.id); setShowEditDialog(false); }}
                  data-testid="delete-stock-btn"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-heda-mute text-center">
                Last updated: {editingStock.updated_at ? formatIST(editingStock.updated_at) : 'N/A'}
                {editingStock.updated_by && ` by ${editingStock.updated_by}`}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <TallyImportDialog
        open={showTallyImport}
        onClose={() => setShowTallyImport(false)}
        onApplied={fetchStocks}
      />
    </div>
  );
}
